import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, pluck } from 'rxjs/operators';
import { Apollo, gql } from 'apollo-angular';
import { MatDialog } from '@angular/material/dialog';

import { ToastService } from 'src/app/core/toast.service';
import { omit } from 'lodash';

import {
  addbanner,
  addbannerdata,
  deleteBannerById,
  deleteBannerCategoryById,
  filterStatusApproveMutation,
  getBannerById,
  getBannerCategoryById,
  getBannersCategoryDatas,
  getBannersDatas,
  updateBanner,
  updateBannerCategory,
  updateBannerCategoryStatus,
  updatebannerdata,
  updateBannerStatusMutation,
  getMonthlyBannersCountQuery,
  getAllbannerData,
  getBannerCountsQuery,
} from '../queries/banners-info-tab-queries';
import { DocumentNode } from 'graphql';
import { LoginService } from 'src/app/core/services/login.service';
@Injectable({
  providedIn: 'root',
})
export class BannersInfoTabService {

  today: string = new Date().toISOString().split('T')[0];
  constructor(
    private apollo: Apollo,
    private _dialog: MatDialog,
    private toast: ToastService,
    private loginService: LoginService
  ) { }

  insertBannerCategory(banner: any) {
    return this.apollo.use('external').mutate({
      mutation: addbannerdata,
      variables: {
        banner: omit(banner, ['id']),
      },
    });
  }
  insertBanner(banner: any) {
    return this.apollo.use('external').mutate({
      mutation: addbanner,
      variables: {
        banner: omit(banner, ['id']),
      },
    });
  }

getBannersCategoryDatas( search?: string) {
  const where: any = {};



  if (search && search.trim() !== '') {
    where.name = { _ilike: `%${search.trim()}%` };
  }

  return this.apollo
    .use('external')
    .watchQuery<any>({
      query: getBannersCategoryDatas,
      variables: { where }
    })
    .valueChanges.pipe(
      map(res => res?.data?.banner_categories || [])
    );
}




  // For full object update
  updateBannerCategory(id: string, data: any): Observable<any> {
    return this.apollo.use('external').mutate({
      mutation: updateBannerCategory,
      variables: { id, data },
    });
  }
  updateBanner(id: string, data: any): Observable<any> {
    return this.apollo.use('external').mutate({
      mutation: updateBanner,
      variables: { id, data },
    });
  }

  // For just status update
  updateBannerCategoryStatus(
    id: string,
    status: 'ACTIVE' | 'INACTIVE'
  ): Observable<any> {
    return this.apollo.use('external').mutate({
      mutation: updateBannerCategoryStatus,
      variables: { id, status },
    });
  }

  deleteBannerCategoryByPk(id: string): Observable<any> {
    return this.apollo.use('external').mutate({
      mutation: deleteBannerCategoryById,
      variables: { id },
    });
  }
  deleteBannerByPk(id: string): Observable<any> {
    return this.apollo.use('external').mutate({
      mutation: deleteBannerById,
      variables: { id },
    });
  }

  getBannerCategoryById(id: string): Observable<any> {
    return this.apollo
      .use('external')
      .query({
        query: getBannerCategoryById,
        variables: { id },
        fetchPolicy: 'no-cache',
      })
      .pipe(map((res: any) => res.data.banner_categories_by_pk));
  }

  getBannerById(id: string): Observable<any> {
    return this.apollo
      .use('external')
      .query({
        query: getBannerById,
        variables: { id },
        fetchPolicy: 'no-cache',
      })
      .pipe(map((res: any) => res.data.banners_by_pk));
  }

  getBannerCounts(query: DocumentNode, variables: any) {
    return this.apollo
      .use('external')
      .watchQuery<any>({
        query,
        variables,
      })
      .valueChanges.pipe(
        map((res) => {
          return {
            all: res?.data?.all?.aggregate?.count ?? 0,
            draft: res?.data?.draft?.aggregate?.count ?? 0,
            pending: res?.data?.pending?.aggregate?.count ?? 0,
            approved: res?.data?.approved?.aggregate?.count ?? 0,
            rejected: res?.data?.rejected?.aggregate?.count ?? 0,
            expired: res?.data?.expired?.aggregate?.count ?? 0,
          };
        })
      );
  }

  getBannersData(
    search: string = '',
    status?: string,
    today?: string,
    limit?: number,
    offset?: number,
    categoryName?: string,
    companyName?: string,
    scheduleDate?: string,
    expiryDate?: string
  ) {
    const loginData = this.loginService.getLoginData();
    const whereClause: any = {};
    const searchTerm = search?.trim() ? `%${search}%` : null;
    const andConditions: any[] = [];

    // Status conditions
    if (status && status !== 'ALL' && status !== 'EXPIRED') {
      andConditions.push({ status: { _eq: status } });
    }

    if (status === 'EXPIRED' && today) {
      andConditions.push({ expiry_date: { _lt: today } });
    }

    if (status === 'DRAFT') {
      andConditions.push({ from_id: { _eq: loginData.id } });
    }

    if (loginData.type === 'SUPPLIER') {
      andConditions.push({ from_id: { _eq: loginData.id } });
    }

    if (status === 'ALL') {
      andConditions.push({
        _or: [
          { status: { _neq: 'DRAFT' } },
          { from_id: { _eq: loginData.id }, status: { _eq: 'DRAFT' } }
        ]
      });
    }

    // Search conditions
    if (searchTerm) {
      andConditions.push({
        _or: [
          { banner_name: { _ilike: searchTerm } },
          { company_name: { _ilike: searchTerm } }
        ]
      });
    }

    //for filter

    /** Category filter */
    if (categoryName) {
      andConditions.push({ category_name: { _eq: categoryName } });
    }

    /** Company filter */
    if (companyName) {
      andConditions.push({ company_name: { _eq: companyName } });
    }

    /** Schedule date filter */
    if (scheduleDate) {
      andConditions.push({ schedule_date: { _gte: scheduleDate } });
    }

    /** Expiry date filter */
    if (expiryDate) {
      andConditions.push({ expiry_date: { _lte: expiryDate } });
    }

    if (andConditions.length > 0) {
      whereClause._and = andConditions;
    }

    return this.apollo
      .use('external')
      .watchQuery<any>({
        query: getBannersDatas,
        variables: { where: whereClause, limit, offset },
      })
      .valueChanges.pipe(
        map((res: any) => ({
          data: res?.data?.banners || [],
          count: res?.data?.banners_aggregate?.aggregate?.count || 0,
        }))
      );
  }

  getBannerFilterOptions(): Observable<any> {
    return this.apollo.query({
      query: gql`
      query GetBannerFilterOptions {
        categories: banners(distinct_on: category_name) {
          category_name
        }
        companies: banners(distinct_on: company_name) {
          company_name
        }
      }
    `
    }).pipe(pluck('data'));
  }




  updateExpiredBannerDataById(ids: string[], status: string) {
    return this.apollo.use('external').mutate({
      mutation: updateBannerStatusMutation,
      variables: { id: ids, status },
    });
  }

  getMonthlyBannersCount(andList): Observable<any> {
    return this.apollo
      .use('external')
      .subscribe({
        query: getMonthlyBannersCountQuery,
        variables: { andList },
      })
      .pipe(pluck('data', 'banners_aggregate', 'aggregate', 'count'));
  }

  filterStatusApprove(
    companyName: string,
    categoryName: string
  ): Observable<any> {
    return this.apollo
      .use('external')
      .watchQuery({
        query: filterStatusApproveMutation,
        variables: {
          company_name: companyName,
          category_name: categoryName,
        },
      })
      .valueChanges.pipe(pluck('data', 'banners'));
  }

  getFilteredBannersData(filters: {
    status: string;
    category_names: string[];
    banner_location: string[];
  }): Observable<any> {
    return this.apollo
      .query<any>({
        query: getAllbannerData,
        variables: filters,
      })
      .pipe(pluck('data', 'banners'));
  }
}
